// src/SubscriptionSettings.js
import { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import { fetchSubscription } from './subscriptionApi';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

const API_BASE = getApiBase();

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const SubscriptionSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile]         = useState(getIsMobile());
  const [loading, setLoading]           = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [cancelling, setCancelling]     = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [cancelled, setCancelled]       = useState(false);
  const [cancelError, setCancelError]   = useState('');

  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile());
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    setLoading(true);
    try {
      const data = await fetchSubscription();
      setSubscription(data && typeof data === 'object' ? data : null);
    } catch (err) {
      console.error('Failed to load subscription:', err);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      const res = await fetch(`${API_BASE}/api/subscribe/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() }
      });
      const data = await res.json();
      if (!res.ok) {
        setCancelError(data.error || 'Cancellation failed. Please contact support@mytoolnow.com');
        return;
      }
      setShowConfirm(false);
      setCancelled(true);
      await loadSubscription();
    } catch (e) {
      setCancelError('Network error. Please try again or contact support@mytoolnow.com');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatMoney = (val) => {
    const n = Number(val);
    return isNaN(n) ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: '800px',
      padding: isPhone ? '0' : '0 12px',
      margin: '0 auto'
    },
    panel: {
      backgroundColor: '#fff7ed',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '20px 5px' : (isMobile ? '20px' : '40px'),
      marginBottom: '18px'
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    },
    infoSection: {
      border: '4px solid black',
      backgroundColor: 'white',
      marginBottom: '16px'
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '14px 16px',
      borderBottom: '2px solid #e5e7eb',
      flexWrap: 'wrap',
      gap: '8px'
    },
    infoRowLast: {
      borderBottom: 'none'
    },
    infoLabel: {
      fontWeight: 900,
      fontSize: '13px',
      textTransform: 'uppercase',
      color: '#555',
      minWidth: '160px'
    },
    infoValue: {
      fontWeight: 900,
      fontSize: '15px',
      textAlign: isMobile ? 'left' : 'right',
      flex: 1
    },
    statusPill: {
      display: 'inline-block',
      padding: '6px 14px',
      border: '3px solid black',
      fontWeight: 900,
      fontSize: '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    migrationNote: {
      fontWeight: 900,
      fontSize: '13px',
      padding: '12px',
      border: '3px solid black',
      backgroundColor: '#ffffcc',
      marginBottom: '16px'
    },
    noRecordBox: {
      fontWeight: 900,
      fontSize: '15px',
      padding: '24px',
      border: '4px solid black',
      backgroundColor: 'white',
      textAlign: 'center'
    },
    cancelBtn: {
      width: '100%',
      padding: '15px',
      marginTop: '24px',
      fontWeight: 900,
      fontSize: '16px',
      border: '4px solid #ef4444',
      backgroundColor: 'black',
      color: '#ef4444',
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    confirmBox: {
      border: '6px solid #ef4444',
      backgroundColor: '#fee2e2',
      padding: '24px',
      marginTop: '20px',
      fontWeight: 900,
      textAlign: 'center'
    },
    confirmTitle: {
      fontSize: '20px',
      fontWeight: 900,
      textTransform: 'uppercase',
      marginBottom: '12px',
      color: '#991b1b'
    },
    confirmText: {
      fontSize: '15px',
      fontWeight: 700,
      lineHeight: 1.6,
      marginBottom: '20px',
      color: '#1a1a1a'
    },
    confirmBtnRow: {
      display: 'flex',
      gap: '12px',
      flexDirection: isMobile ? 'column' : 'row'
    },
    yesCancelBtn: {
      flex: 1,
      padding: '14px',
      fontWeight: 900,
      fontSize: '15px',
      border: '4px solid black',
      backgroundColor: '#ef4444',
      color: 'white',
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    keepBtn: {
      flex: 1,
      padding: '14px',
      fontWeight: 900,
      fontSize: '15px',
      border: '4px solid #E8761D',
      backgroundColor: 'black',
      color: '#FFA500',
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    successBox: {
      border: '6px solid #22c55e',
      backgroundColor: '#dcfce7',
      padding: '24px',
      marginTop: '20px',
      fontWeight: 900,
      textAlign: 'center',
      lineHeight: 1.8
    },
    successTitle: {
      fontSize: '22px',
      fontWeight: 900,
      textTransform: 'uppercase',
      marginBottom: '12px',
      color: '#166534'
    },
    errorBox: {
      border: '4px solid #ef4444',
      backgroundColor: '#fee2e2',
      padding: '14px',
      marginTop: '12px',
      fontWeight: 900,
      fontSize: '14px'
    }
  };

  const STATUS_COLORS = {
    lifetime:  '#3b82f6',
    active:    '#22c55e',
    trial:     '#eab308',
    past_due:  '#ef4444',
    cancelled: '#ef4444',
    paused:    '#f59e0b'
  };

  const renderContent = () => {
    if (!subscription) {
      return (
        <div style={styles.noRecordBox}>
          No subscription details available yet for this account.<br />
          If you just signed up, your plan details may take a moment to appear.
        </div>
      );
    }

    const status      = String(subscription.status || 'active').toLowerCase();
    const pillColor   = STATUS_COLORS[status] || '#64748b';
    const isMigration = subscription.migration_default === true;
    const isActive    = status === 'active';

    const rows = [
      { label: 'STATUS',            value: <span style={{ ...styles.statusPill, backgroundColor: pillColor, color: ['active','lifetime'].includes(status) ? '#000' : '#fff' }}>{status.toUpperCase().replace('_', ' ')}</span> },
      { label: 'PLAN NAME',         value: subscription.planName      || '—' },
      { label: 'PAYMENT METHOD',    value: subscription.paymentMethod ? String(subscription.paymentMethod).toUpperCase() : '—' },
      { label: 'MONTHLY FEE',       value: subscription.monthlyFee != null ? formatMoney(subscription.monthlyFee) : '—' },
      { label: 'NEXT BILLING DATE', value: formatDate(subscription.nextBillingDate) },
      { label: 'MEMBER SINCE',      value: formatDate(subscription.created_at) },
    ];

    return (
      <>
        {isMigration && (
          <div style={styles.migrationNote}>
            ⚠️ No subscription details available yet for this account. If you just signed up, your plan details may take a moment to appear.
          </div>
        )}

        <div style={styles.infoSection}>
          {rows.map((row, i) => (
            <div
              key={row.label}
              style={{ ...styles.infoRow, ...(i === rows.length - 1 ? styles.infoRowLast : {}) }}
            >
              <span style={styles.infoLabel}>{row.label}</span>
              <span style={styles.infoValue}>{row.value}</span>
            </div>
          ))}
        </div>

        {/* ── SUCCESS MESSAGE after cancel ── */}
        {cancelled && (
          <div style={styles.successBox}>
            <div style={styles.successTitle}>Sorry To See You Go!</div>
            <div>We will miss you! For peace of mind, your data is always secure and will be right here waiting when you return. You'll keep full access until your current billing period ends.</div>
          </div>
        )}

        {/* ── CONFIRMATION BOX ── */}
        {showConfirm && !cancelled && (
          <div style={styles.confirmBox}>
            <div style={styles.confirmTitle}>⚠️ Are You Sure?</div>
            <div style={styles.confirmText}>
              You are about to cancel your MyTool NOW subscription.<br />
              You will keep full access until your current billing period ends.<br />
              Your data will always be safe and waiting for you if you return.
            </div>
            {cancelError && (
              <div style={styles.errorBox}>{cancelError}</div>
            )}
            <div style={styles.confirmBtnRow}>
              <button
                className="jf-sub-hover"
                style={{ ...styles.yesCancelBtn, opacity: cancelling ? 0.6 : 1, cursor: cancelling ? 'not-allowed' : 'pointer' }}
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? '⏳ CANCELLING...' : '✕ YES, CANCEL MY SUBSCRIPTION'}
              </button>
              <button
                className="jf-sub-hover"
                style={styles.keepBtn}
                onClick={() => { setShowConfirm(false); setCancelError(''); }}
                disabled={cancelling}
              >
                ✅ NO, KEEP MY SUBSCRIPTION
              </button>
            </div>
          </div>
        )}

        {/* ── CANCEL BUTTON — active subscribers only, never lifetime ── */}
        {isActive && !showConfirm && !cancelled && (
          <button
            className="jf-sub-cancel"
            style={styles.cancelBtn}
            onClick={() => { setShowConfirm(true); setCancelError(''); }}
          >
            ✕ CANCEL SUBSCRIPTION
          </button>
        )}
      </>
    );
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-sub-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
        .jf-sub-cancel:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
      `}</style>
      <div style={styles.contentWrapper}>
        <TitleBar
          icon="💳"
          title="YOUR SUBSCRIPTION"
          accent="#E8761D"
          right={
            <button
              className="jf-sub-hover"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING SUBSCRIPTION...</div>
          ) : (
            renderContent()
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSettings;
